/**
 * usePdfPageExtraction - Hook for extracting pages from a PDF
 *
 * Uses server-side extraction with PyMuPDF for reliability.
 * Handles encrypted/restricted PDFs (like Acrobat Sign) that client-side
 * libraries like pdf-lib cannot process correctly.
 */
import { useCallback, useState } from 'react';

import { useExtractPagesMutation } from '@/services/api/blobService';

import type { ExtractionProgress, ExtractionResult } from './types';

interface UsePdfPageExtractionOptions {
  matterId: string;
  onSuccess?: (result: ExtractionResult) => void;
  onError?: (error: Error) => void;
}

export const usePdfPageExtraction = ({
  matterId,
  onSuccess,
  onError,
}: UsePdfPageExtractionOptions) => {
  const [progress, setProgress] = useState<ExtractionProgress>({
    status: 'idle',
    progress: 0,
  });

  const [extractPagesServer] = useExtractPagesMutation();

  /**
   * Extract pages from a PDF and create a new blob
   */
  const extractAndUpload = useCallback(
    async ({
      sourceBlobId,
      pageNumbers,
      collectionId,
      filename,
    }: {
      sourceBlobId: string;
      sourceUrl?: string; // Not used but kept for API compatibility
      sourceName?: string; // Not used but kept for API compatibility
      pageNumbers: number[];
      collectionId: string;
      filename?: string;
    }): Promise<ExtractionResult | null> => {
      try {
        setProgress({ status: 'extracting', progress: 25 });

        const response = await extractPagesServer({
          matterId,
          blobId: sourceBlobId,
          body: {
            page_numbers: pageNumbers,
            target_collection_id: collectionId,
            filename: filename || null,
          },
        }).unwrap();

        setProgress({ status: 'complete', progress: 100 });

        const result: ExtractionResult = {
          blobId: response.blob_id,
          filename: response.filename,
          pageCount: response.page_count,
          collectionName: response.collection_name,
        };

        onSuccess?.(result);
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Extraction failed';
        setProgress({ status: 'error', progress: 0, error: errorMessage });
        onError?.(error instanceof Error ? error : new Error(errorMessage));
        return null;
      }
    },
    [matterId, extractPagesServer, onSuccess, onError],
  );

  /**
   * Reset the extraction state
   */
  const reset = useCallback(() => {
    setProgress({ status: 'idle', progress: 0 });
  }, []);

  return {
    progress,
    extractAndUpload,
    reset,
    isExtracting:
      progress.status === 'loading-pdf' ||
      progress.status === 'extracting' ||
      progress.status === 'uploading',
    isComplete: progress.status === 'complete',
    hasError: progress.status === 'error',
  };
};

export default usePdfPageExtraction;
